import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Trash2, FileText, DollarSign, Clock, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import InvoiceEditor from "./InvoiceEditor";

const money = (n) => "$" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const statusStyle = {
  Paid: "bg-green-500/12 text-green-400", Partial: "bg-amber-500/12 text-amber-400",
  Unpaid: "bg-red-500/12 text-red-400", Draft: "bg-zinc-500/20 text-zinc-400",
};

export default function InvoiceManager({ api, module, title, subtitle, sourceConfig, allowBranding }) {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState("all");
  const [editingId, setEditingId] = useState(null);
  const [picking, setPicking] = useState(false);
  const [sourceId, setSourceId] = useState("");
  const [creating, setCreating] = useState(false);
  const [searchQ, setSearchQ] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();

  // Optional deep link: /invoices?open=<id> opens that invoice and drops the
  // param, so a back-navigation doesn't reopen it. No param, no behaviour change.
  useEffect(() => {
    const open = searchParams.get("open");
    if (!open) return;
    setEditingId(open);
    const next = new URLSearchParams(searchParams);
    next.delete("open");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const invKey = ["invoices", module];
  const { data: invoices = [], isLoading } = useQuery({
    queryKey: invKey,
    queryFn: async () => (await api.get("/invoices")).data,
  });
  const { data: summary } = useQuery({ queryKey: ["invoices-summary", module], queryFn: async () => (await api.get("/invoices/summary")).data });
  const { data: sources = [] } = useQuery({
    queryKey: ["invoice-sources", module],
    queryFn: async () => (await api.get(sourceConfig.listPath)).data,
    enabled: picking,
  });

  const refresh = () => { qc.invalidateQueries({ queryKey: invKey }); qc.invalidateQueries({ queryKey: ["invoices-summary", module] }); };

  const filtered = statusFilter === "all" ? invoices : invoices.filter((i) => i.status === statusFilter);

  const createFromSource = async (id) => {
    const useId = id || sourceId;
    if (!useId) { toast.error(`Select a ${sourceConfig.label.toLowerCase()}`); return; }
    setCreating(true);
    try {
      const { data } = await api.post(`/invoices/from-source/${useId}`);
      setPicking(false); setSourceId(""); setSearchQ({}); refresh(); setEditingId(data.id);
      toast.success("Invoice created");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create invoice"); } finally { setCreating(false); }
  };

  const createBlank = async () => {
    setCreating(true);
    try {
      const { data } = await api.post(`/invoices`, { source_type: "manual", items: [] });
      setPicking(false); setSourceId(""); setSearchQ({}); refresh(); setEditingId(data.id);
      toast.success("Blank invoice created — fill in the details");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create invoice"); } finally { setCreating(false); }
  };

  const searchFields = sourceConfig.search;
  const hasQuery = searchFields && searchFields.some((f) => (searchQ[f.key] || "").trim());
  const results = (searchFields && hasQuery)
    ? sources.filter((b) => searchFields.every((f) => {
        const v = (searchQ[f.key] || "").trim().toLowerCase();
        if (!v) return true;
        return String(f.get(b) || "").toLowerCase().includes(v);
      }))
      .sort((a, b) => {
        // Ongoing/current rentals first, then most-recent by date
        const rank = (x) => (x.status === "current" ? 0 : x.status === "confirmed" || x.status === "pre_booking" ? 1 : 2);
        const r = rank(a) - rank(b);
        if (r !== 0) return r;
        return String(b.start_datetime || "").localeCompare(String(a.start_datetime || ""));
      })
      .slice(0, 60)
    : [];

  const del = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this invoice?")) return;
    try { await api.delete(`/invoices/${id}`); refresh(); toast.success("Invoice deleted"); }
    catch { toast.error("Failed to delete"); }
  };

  return (
    <div data-testid={`${module}-invoices-page`}>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl mb-1">{title}</h1>
          <p className="text-muted-foreground">{subtitle}</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="invoice-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {["Draft", "Unpaid", "Partial", "Paid"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={() => setPicking(true)} data-testid="new-invoice-button"><Plus className="h-4 w-4 mr-2" />New Invoice</Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <Card className="p-4 flex items-center gap-3" data-testid="invoice-kpi-outstanding"><div className="h-10 w-10 rounded-full bg-red-500/12 text-red-400 flex items-center justify-center"><Clock className="h-5 w-5" /></div><div><p className="text-xs text-muted-foreground">Outstanding</p><p className="text-xl font-bold">{money(summary?.outstanding)}</p></div></Card>
        <Card className="p-4 flex items-center gap-3" data-testid="invoice-kpi-collected"><div className="h-10 w-10 rounded-full bg-green-500/12 text-green-400 flex items-center justify-center"><DollarSign className="h-5 w-5" /></div><div><p className="text-xs text-muted-foreground">Collected</p><p className="text-xl font-bold">{money(summary?.collected)}</p></div></Card>
        <Card className="p-4 flex items-center gap-3" data-testid="invoice-kpi-count"><div className="h-10 w-10 rounded-full bg-blue-500/12 text-blue-400 flex items-center justify-center"><FileText className="h-5 w-5" /></div><div><p className="text-xs text-muted-foreground">Invoices</p><p className="text-xl font-bold">{summary?.count ?? 0} <span className="text-xs font-normal text-muted-foreground">· {summary?.unpaid ?? 0} unpaid</span></p></div></Card>
      </div>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invoice #</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Issued</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right">Balance</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">No invoices yet. Create one from a {sourceConfig.label.toLowerCase()}.</TableCell></TableRow>
            ) : filtered.map((i) => (
              <TableRow key={i.id} className="cursor-pointer" data-testid={`invoice-row-${i.id}`} onClick={() => setEditingId(i.id)}>
                <TableCell className="font-mono text-sm">{i.invoice_number}</TableCell>
                <TableCell className="font-medium">{i.customer_name || "—"}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{i.issue_date}</TableCell>
                <TableCell><Badge className={`border-0 ${statusStyle[i.status]}`}>{i.status}</Badge></TableCell>
                <TableCell className="text-right font-mono">{money(i.total)}</TableCell>
                <TableCell className="text-right font-mono">{money(i.balance)}</TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost" size="icon" title="Copy client pay link" data-testid={`copy-pay-link-${i.id}`}
                    disabled={i.status === "Paid" || i.status === "Draft"}
                    onClick={(e) => {
                      e.stopPropagation();
                      const url = `${window.location.origin}/pay/${i.id}`;
                      navigator.clipboard.writeText(url)
                        .then(() => toast.success(`Pay link copied — send it to ${i.customer_name || "the client"}.`))
                        .catch(() => toast.error("Could not copy — copy this URL manually: " + url));
                    }}
                  ><LinkIcon className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={(e) => del(i.id, e)} data-testid={`del-invoice-${i.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Source picker */}
      <Dialog open={picking} onOpenChange={(o) => { setPicking(o); if (!o) { setSearchQ({}); setSourceId(""); } }}>
        <DialogContent className="max-w-2xl" data-testid="invoice-source-picker">
          <DialogHeader><DialogTitle>New Invoice from {sourceConfig.label}</DialogTitle><DialogDescription>{searchFields ? `Search to find a ${sourceConfig.label.toLowerCase()}, or start a blank invoice below.` : `Select a ${sourceConfig.label.toLowerCase()} to auto-generate an invoice, or start a blank one below.`}</DialogDescription></DialogHeader>

          {searchFields ? (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {searchFields.map((f) => (
                  <Input key={f.key} placeholder={f.label} value={searchQ[f.key] || ""} onChange={(e) => setSearchQ((o) => ({ ...o, [f.key]: e.target.value }))} data-testid={`invoice-search-${f.key}`} autoFocus={f.key === searchFields[0].key} />
                ))}
              </div>
              <div className="border border-border rounded-md max-h-80 overflow-y-auto divide-y divide-border" data-testid="invoice-search-results">
                {!hasQuery ? (
                  <p className="p-6 text-center text-sm text-muted-foreground">Start typing to find a {sourceConfig.label.toLowerCase()}.</p>
                ) : results.length === 0 ? (
                  <p className="p-6 text-center text-sm text-muted-foreground">No matching {sourceConfig.label.toLowerCase()}s.</p>
                ) : results.map((b) => (
                  <button key={sourceConfig.id(b)} disabled={creating} onClick={() => createFromSource(sourceConfig.id(b))} className="w-full text-left px-4 py-2.5 hover:bg-accent transition-colors flex items-center justify-between gap-3" data-testid={`invoice-search-result-${sourceConfig.id(b)}`}>
                    <span className="text-sm flex items-center gap-2">{b.status === "current" && <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-green-500/12 text-green-400 font-semibold">Ongoing</span>}{sourceConfig.optionLabel(b)}</span>
                    <span className="text-xs text-primary shrink-0">Create →</span>
                  </button>
                ))}
              </div>
              {hasQuery && results.length === 60 && <p className="text-xs text-muted-foreground">Showing first 60 matches — refine your search to narrow down.</p>}
            </div>
          ) : (
            <>
              <Select value={sourceId} onValueChange={setSourceId}>
                <SelectTrigger data-testid="invoice-source-select"><SelectValue placeholder={`Select a ${sourceConfig.label.toLowerCase()}…`} /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {sources.map((s) => <SelectItem key={sourceConfig.id(s)} value={String(sourceConfig.id(s))}>{sourceConfig.optionLabel(s)}</SelectItem>)}
                </SelectContent>
              </Select>
              <DialogFooter>
                <Button variant="outline" onClick={() => setPicking(false)}>Cancel</Button>
                <Button onClick={() => createFromSource()} disabled={creating} data-testid="invoice-create-confirm">{creating ? "Creating…" : "Create Invoice"}</Button>
              </DialogFooter>
            </>
          )}

          <div className="border-t border-border pt-3 mt-1">
            <Button variant="outline" className="w-full" onClick={createBlank} disabled={creating} data-testid="invoice-create-blank">
              <Plus className="h-4 w-4 mr-2" /> Start a blank invoice — enter all job info manually
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {editingId && (
        <InvoiceEditor api={api} invoiceId={editingId} module={module} allowBranding={allowBranding} onClose={() => setEditingId(null)} onSaved={refresh} />
      )}
    </div>
  );
}
